<script setup>
/**
 * H3（minimax-h3）视频生成的默认技术参数（`video_model.params`）——见
 * apps/visual-game-server/src/assembly/intent-video-lemonboss.js 里 loadH3DefaultParams 的注释：
 * 这些字段代码统一从这张表读，规划 agent 从不设置。这个页面是它专属的可视化 CRUD，
 * 复用「模型能力」后台已有的 `video/models` 保存接口，不需要新的后端代码。
 *
 * 参数取值全部做成下拉选择，选项照抄 H3 Console（http://192.168.110.67:8080/newtask.html）
 * 实际暴露给运营的那几组值——那是真实提交给 H3 服务、经过实测验证的合法组合，不是随手编的范围。
 * width/height 在那边是绑在一起的一个「分辨率」下拉，这里也合并展示成一行，避免选出服务端会
 * 400 的宽高错配组合；ref_image_short_edge 那边完全没有暴露对应控件，没有权威取值列表可抄，
 * 只能保留成普通输入框。
 */
import { computed, onMounted, ref } from 'vue'
import { Badge, Button, ConfirmAction, EmptyState, FormField, Input, Modal, PageHeader, Section, Select, Table, useToast } from '@drama/ui'
import { Pencil, Plus, RefreshCw, Trash2 } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import { fetchModelCapabilityCatalog, saveCapabilityModel } from '../../api/model-capabilities.js'

/** 迁移脚本（202608250002/5_*.js）和 intent-video-lemonboss.js 的 H3_PROVIDER 用的同一个代码。 */
const H3_CAPABILITY_CODE = 'minimax_h3'

const RESOLUTION_OPTIONS = [
  { value: '1344x768', label: '1344×768（官方默认分辨率）', width: 1344, height: 768 },
  { value: '832x480', label: '832×480（标准档）', width: 832, height: 480 },
  { value: '448x256', label: '448×256（长时长档）', width: 448, height: 256 },
  { value: '1024x576', label: '1024×576（高清档）', width: 1024, height: 576 },
]
const FRAME_OPTIONS = [
  { value: 124, label: '124（约 5s）' },
  { value: 243, label: '243（约 10s）' },
  { value: 362, label: '362（约 15s，默认）' },
  { value: 412, label: '412（约 17s，1344×768 实测上限）' },
]
const STEPS_OPTIONS = [
  { value: 20, label: '20 步' },
  { value: 50, label: '50 步（全质量，约 9.6 倍耗时）' },
]
const TURBO_OPTIONS = [
  { value: 'true', label: '开（4 步蒸馏 LoRA，快）' },
  { value: 'false', label: '关（多步，慢但更稳）' },
]
const SEED_OPTIONS = [0, 42, 123, 2024].map(value => ({ value, label: String(value) }))
/** key -> 下拉选项。不在这里面的 key（目前只有 ref_image_short_edge）没有权威取值列表，退回输入框。 */
const FIELD_SELECT_OPTIONS = { frames: FRAME_OPTIONS, steps: STEPS_OPTIONS, turbo: TURBO_OPTIONS, seed: SEED_OPTIONS }
/** 「新增参数」可选的字段，以及尚未支持下拉的兜底字段。 */
const ADDABLE_FIELDS = [
  { key: 'resolution', label: 'resolution (width + height)' },
  { key: 'frames', label: 'frames' },
  { key: 'steps', label: 'steps' },
  { key: 'turbo', label: 'turbo' },
  { key: 'seed', label: 'seed' },
  { key: 'ref_image_short_edge', label: 'ref_image_short_edge' },
]
/** 除 width/height（合并进 resolution 行）外，剩下按原始 key 单独展示的技术字段。 */
const SIMPLE_FIELD_KEYS = ['frames', 'steps', 'turbo', 'seed', 'ref_image_short_edge']

const { t } = useI18n()
const toast = useToast()

const catalog = ref(null)
const loading = ref(false)
const error = ref('')

const h3Models = computed(() => {
  if (!catalog.value) return []
  const capabilityIds = new Set(
    catalog.value.video.provider_capabilities
      .filter(row => row.code === H3_CAPABILITY_CODE)
      .map(row => row.id),
  )
  return catalog.value.video.models.filter(row => capabilityIds.has(row.provider_capability_id))
})

onMounted(loadCatalog)

async function loadCatalog() {
  loading.value = true
  error.value = ''
  try {
    catalog.value = await fetchModelCapabilityCatalog()
  } catch (cause) {
    error.value = cause.message
  } finally {
    loading.value = false
  }
}

function displayRows(model) {
  const params = model.params || {}
  const rows = []
  if ('width' in params || 'height' in params) {
    rows.push({ kind: 'resolution', key: 'resolution', width: params.width, height: params.height })
  }
  for (const key of SIMPLE_FIELD_KEYS) {
    if (key in params) rows.push({ kind: 'field', key, value: params[key] })
  }
  // 兜底：历史上手填过的、不在已知技术字段里的 key 也照样展示出来，不能删除时看不见它。
  for (const key of Object.keys(params)) {
    if (key === 'width' || key === 'height' || SIMPLE_FIELD_KEYS.includes(key)) continue
    rows.push({ kind: 'field', key, value: params[key] })
  }
  return rows
}

function resolutionOptionFor(width, height) {
  return RESOLUTION_OPTIONS.find(option => option.width === width && option.height === height) ?? null
}

function resolutionValueText(row) {
  return resolutionOptionFor(row.width, row.height)?.label ?? `${row.width}×${row.height}`
}

function valueToText(value) {
  return typeof value === 'string' ? value : JSON.stringify(value)
}

function parseValueText(text) {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function valueType(value) {
  if (Array.isArray(value)) return 'array'
  if (value === null) return 'null'
  return typeof value
}

function addableFields(model) {
  const params = model.params || {}
  return ADDABLE_FIELDS.filter(field => (
    field.key === 'resolution' ? !('width' in params && 'height' in params) : !(field.key in params)
  ))
}

function defaultFormValue(key) {
  if (key === 'resolution') return ''
  return FIELD_SELECT_OPTIONS[key]?.[0].value ?? ''
}

// --- 增/改共用一个弹框 ---
const formModalOpen = ref(false)
const formMode = ref('add')
const formModel = ref(null)
const formKey = ref('')
const formValue = ref('')
const submitted = ref(false)
const saving = ref(false)

const formKeyOptions = computed(() => (
  formModel.value ? addableFields(formModel.value).map(field => ({ value: field.key, label: field.label })) : []
))
const formValueOptions = computed(() => (formKey.value === 'resolution' ? RESOLUTION_OPTIONS : FIELD_SELECT_OPTIONS[formKey.value] ?? null))
const valueError = computed(() => {
  if (!submitted.value) return ''
  if (formKey.value === 'resolution' && !formValue.value) return t('visualGameAdmin.llm.h3Config.errors.resolutionRequired')
  return ''
})

function setFormKey(key) {
  formKey.value = key
  formValue.value = defaultFormValue(key)
}

function openAdd(model) {
  const addable = addableFields(model)
  if (addable.length === 0) return
  formMode.value = 'add'
  formModel.value = model
  setFormKey(addable[0].key)
  submitted.value = false
  formModalOpen.value = true
}

function openEdit(model, row) {
  formMode.value = 'edit'
  formModel.value = model
  formKey.value = row.key
  if (row.kind === 'resolution') {
    formValue.value = resolutionOptionFor(row.width, row.height)?.value ?? ''
  } else if (row.key === 'turbo') {
    formValue.value = row.value ? 'true' : 'false'
  } else if (FIELD_SELECT_OPTIONS[row.key]) {
    formValue.value = row.value
  } else {
    formValue.value = valueToText(row.value)
  }
  submitted.value = false
  formModalOpen.value = true
}

async function submitParam() {
  submitted.value = true
  if (valueError.value) return
  const nextParams = { ...(formModel.value.params || {}) }
  if (formKey.value === 'resolution') {
    const preset = RESOLUTION_OPTIONS.find(option => option.value === formValue.value)
    nextParams.width = preset.width
    nextParams.height = preset.height
  } else if (formKey.value === 'turbo') {
    nextParams.turbo = formValue.value === 'true'
  } else if (FIELD_SELECT_OPTIONS[formKey.value]) {
    nextParams[formKey.value] = formValue.value
  } else {
    nextParams[formKey.value] = parseValueText(formValue.value)
  }
  saving.value = true
  try {
    catalog.value = await persistParams(formModel.value, nextParams)
    formModalOpen.value = false
  } catch (cause) {
    toast.error(cause.message, { duration: 0 })
  } finally {
    saving.value = false
  }
}

function deleteRow(model, row) {
  const nextParams = { ...(model.params || {}) }
  if (row.kind === 'resolution') {
    delete nextParams.width
    delete nextParams.height
  } else {
    delete nextParams[row.key]
  }
  return persistParams(model, nextParams)
}

function persistParams(model, params) {
  return saveCapabilityModel('video', {
    model: model.model,
    provider_capability_id: model.provider_capability_id,
    pricing: model.pricing,
    params,
    enabled: model.enabled === 1,
  })
}
</script>

<template>
  <div class="h3-config vga-page">
    <PageHeader :title="t('visualGameAdmin.llm.h3Config.title')">
      <template #actions>
        <Button variant="secondary" size="sm" :loading="loading" @click="loadCatalog">
          <RefreshCw :size="16" />
          {{ t('visualGameAdmin.llm.h3Config.refresh') }}
        </Button>
      </template>
    </PageHeader>
    <p class="h3-config__description">{{ t('visualGameAdmin.llm.h3Config.description') }}</p>

    <p v-if="error" class="vga-error">{{ error }}</p>

    <template v-if="catalog">
      <EmptyState
        v-if="h3Models.length === 0"
        :title="t('visualGameAdmin.llm.h3Config.notFound')"
      />
      <Section
        v-for="model in h3Models"
        :key="model.id"
        :title="model.model"
        :padded="false"
      >
        <template #actions>
          <Badge :variant="model.enabled ? 'success' : 'neutral'">
            {{ t(`visualGameAdmin.llm.capabilities.status.${model.enabled ? 'enabled' : 'disabled'}`) }}
          </Badge>
          <Button
            variant="primary"
            size="sm"
            :disabled="addableFields(model).length === 0"
            @click="openAdd(model)"
          >
            <Plus :size="16" />
            {{ t('visualGameAdmin.llm.h3Config.addParam') }}
          </Button>
        </template>

        <Table
          row-key="key"
          :rows="displayRows(model)"
          :columns="[
            { key: 'key', label: t('visualGameAdmin.llm.h3Config.table.key'), width: '240px', mono: true },
            { key: 'value', label: t('visualGameAdmin.llm.h3Config.table.value'), width: '280px', wrap: true },
            { key: 'type', label: t('visualGameAdmin.llm.h3Config.table.type'), width: '110px' },
            { key: 'actions', label: t('visualGameAdmin.llm.h3Config.table.actions'), width: '160px', align: 'right' },
          ]"
        >
          <template #value="{ row }">
            {{ row.kind === 'resolution' ? resolutionValueText(row) : valueToText(row.value) }}
          </template>
          <template #type="{ row }">
            <Badge variant="neutral">{{ row.kind === 'resolution' ? 'resolution' : valueType(row.value) }}</Badge>
          </template>
          <template #actions="{ row }">
            <div class="h3-config__actions">
              <Button variant="ghost" size="sm" @click="openEdit(model, row)">
                <Pencil :size="14" />
                {{ t('visualGameAdmin.common.edit') }}
              </Button>
              <ConfirmAction
                danger
                :label="t('visualGameAdmin.common.delete')"
                :confirm-text="t('visualGameAdmin.llm.h3Config.confirmDelete', { key: row.key })"
                :cancel-label="t('visualGameAdmin.common.cancel')"
                :ok-label="t('visualGameAdmin.common.delete')"
                :action="() => deleteRow(model, row)"
                @done="catalog = $event"
              />
            </div>
          </template>
        </Table>
      </Section>
    </template>

    <Modal
      :open="formModalOpen"
      :title="t(`visualGameAdmin.llm.h3Config.form.${formMode === 'add' ? 'addTitle' : 'editTitle'}`)"
      width="440px"
      @update:open="formModalOpen = $event"
    >
      <form class="h3-config-form" @submit.prevent="submitParam">
        <FormField :label="t('visualGameAdmin.llm.h3Config.form.key')">
          <Select v-if="formMode === 'add'" :model-value="formKey" :options="formKeyOptions" block @update:model-value="setFormKey" />
          <Input v-else :model-value="formKey" disabled />
        </FormField>
        <FormField :label="t('visualGameAdmin.llm.h3Config.form.value')" :error="valueError">
          <Select
            v-if="formValueOptions"
            v-model="formValue"
            :options="formValueOptions"
            :placeholder="t('visualGameAdmin.llm.h3Config.form.selectPlaceholder')"
            :invalid="Boolean(valueError)"
            block
          />
          <template v-else>
            <Input v-model="formValue" />
            <p class="h3-config-form__hint">{{ t('visualGameAdmin.llm.h3Config.form.valueHint') }}</p>
          </template>
        </FormField>
      </form>
      <template #footer>
        <Button variant="ghost" @click="formModalOpen = false">{{ t('visualGameAdmin.common.cancel') }}</Button>
        <Button variant="primary" :loading="saving" @click="submitParam">{{ t('visualGameAdmin.common.save') }}</Button>
      </template>
    </Modal>
  </div>
</template>

<style scoped lang="scss">
.h3-config {
  display: grid;
  gap: var(--drama-space-lg);

  &__description {
    margin: calc(-1 * var(--drama-space-md)) 0 0;
    color: var(--drama-color-text-muted);
    font-size: var(--drama-font-size-sm);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--drama-space-xs);
  }
}

.h3-config-form {
  display: grid;
  gap: var(--drama-space-lg);

  &__hint {
    margin: var(--drama-space-xs) 0 0;
    color: var(--drama-color-text-muted);
    font-size: var(--drama-font-size-xs);
  }
}
</style>
